// src/lib/offline/init.ts
import {deleteDB} from 'idb';
import {initConnectivityListeners, useConnectivityStore} from './connectivity';
import {createMutationQueue, type MutationQueue} from './queue';
import {useQueueStore, resetQueueStore} from './queue-store';
import {replayQueue} from './replay';
import {installOfflineInterceptor, api} from '@/api/client';

/** Singleton queue instance — null until initOffline() is called. */
let queue: MutationQueue | null = null;

/** Active cleanup function — used for double-init idempotency guard. */
let activeCleanup: (() => void) | null = null;

/** The userId for the current offline session — needed for database cleanup. */
let activeUserId: string | null = null;

/**
 * Get the active mutation queue.
 * Throws if initOffline() hasn't been called yet.
 */
export function getMutationQueue(): MutationQueue {
  if (!queue) {
    throw new Error('Mutation queue not initialized. Call initOffline() first.');
  }
  return queue;
}

/** @internal — only for testing */
export function resetInitOffline(): void {
  if (activeCleanup) activeCleanup();
  queue = null;
  activeCleanup = null;
  activeUserId = null;
}

/**
 * Delete all offline data for the current user.
 *
 * Drops both per-user IndexedDB databases:
 * - `dyna-queue-{userId}` (mutation queue)
 * - `dyna-cache-{userId}` (TQ cache persister)
 *
 * Also resets the queue Zustand store.
 *
 * **PDPA mandatory:** Must be called on logout to ensure no user data
 * persists in IndexedDB after session ends.
 */
export async function deleteOfflineData(): Promise<void> {
  // Delete the mutation queue database
  if (queue) {
    await queue.deleteDatabase();
    queue = null;
  }

  // Delete the TQ cache persister database
  if (activeUserId) {
    await deleteDB(`dyna-cache-${activeUserId}`);
  }

  // Reset Zustand stores
  resetQueueStore();

  activeUserId = null;
}

/**
 * Initialize all offline infrastructure:
 *
 * 1. Start connectivity listeners (browser events + heartbeat)
 * 2. Create the mutation queue (per-user IndexedDB)
 * 3. Install the Axios offline interceptor
 * 4. Subscribe to connectivity changes to trigger replay on reconnect
 *
 * Call once at app startup (e.g., in __root.tsx useEffect).
 * Returns a cleanup function for unmount.
 *
 * Idempotent: calling twice tears down the first instance before
 * re-initializing (React strict mode safety).
 *
 * @param userId - Authenticated user ID for per-user IndexedDB databases.
 *                 PDPA: Each user gets isolated storage; never use a shared namespace.
 */
export function initOffline(userId: string): () => void {
  if (!userId) {
    throw new Error('initOffline requires an authenticated userId. Do not call before login.');
  }
  // Tear down previous instance if re-initialized (strict mode safety)
  if (activeCleanup) activeCleanup();

  // 1. Connectivity listeners + heartbeat
  const cleanupConnectivity = initConnectivityListeners();

  // 2. Mutation queue (per-user IndexedDB)
  activeUserId = userId;
  queue = createMutationQueue(userId);

  // 2b. Purge stale mutations older than 7 days
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  void queue.purgeStale(SEVEN_DAYS_MS);

  // 3. Axios interceptor — enqueues failed mutations to IndexedDB
  const interceptorId = installOfflineInterceptor(async (mutation) => {
    const q = getMutationQueue();
    const id = await q.enqueue(mutation);

    // Update queue store so SyncStatusIndicator reflects the new pending count
    const count = await q.getCount();
    useQueueStore.getState().setPendingCount(count);

    return id;
  });

  // 4. Replay on reconnect — when isOnline transitions false → true
  let previousIsOnline = useConnectivityStore.getState().isOnline;

  const unsubscribeConnectivity = useConnectivityStore.subscribe((state) => {
    const wasOffline = !previousIsOnline;
    const isNowOnline = state.isOnline;
    previousIsOnline = isNowOnline;

    if (wasOffline && isNowOnline && queue) {
      void replayQueue(queue).catch(() => {
        // Replay failed — will re-trigger on next online event.
        // Queue persists in IndexedDB; no data loss.
      });
    }
  });

  // Cleanup function for unmount
  const cleanup = () => {
    cleanupConnectivity();
    unsubscribeConnectivity();
    api.interceptors.response.eject(interceptorId);
    queue = null;
    activeCleanup = null;
  };

  activeCleanup = cleanup;
  return cleanup;
}
